import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import { useContextoResumoAcoes } from './ContextoResumoInscricao.jsx';
import { buildTabContent, getStepSections, listarErrosSecao, STEP_IDS, temErroNosCampos } from './inscricaoSections';
import BlocoErro from '../../../shared/listaerros/BlocoErro.js';
import { criarColunaAcoes } from './AcoesLinha.jsx';
import useFetch from "../../../../hooks/useFetch.js";
import { URL_FORMACOES, URL_PROCESSO_SELETIVO, URL_PROCESSO_SELETIVO_ENEM } from "../../../../constants";
import {
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Select,
    Tooltip
} from '@material-ui/core';
import Grid from "@material-ui/core/Grid";
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import useFormularioInscricao from './useFormularioInscricao';
import * as Yup from "yup";
import Add from '@material-ui/icons/AddOutlined';
import Delete from '@material-ui/icons/DeleteOutlined';
import Edit from '@material-ui/icons/EditOutlined';
import FileSelector from "../../../shared/fileselector/FileSelector.js";
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ToolBar from '../../../shared/toolbar/ToolBar';
import useDialogState from '../../../../hooks/useDialogState.js';
import DialogEscolaridade from './DialogEscolaridade.jsx';
import { containedButton, tituloSecao } from "../../../../utils/styles";
import { criarConfiguracaoFormulario, GRUPOS_FORMULARIO } from './formularioConfiguracao';
import { arquivoParaBase64 } from '../../../../utils/arquivo';
import useErrorMessage from "../../../../hooks/useErrorMessage";

const useStyles = makeStyles(theme => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    addButton: {
        marginBottom: theme.spacing(2)
    },
    dialogContent: {
        paddingTop: theme.spacing(2)
    },
    textMulti: {
        backgroundColor: 'white',
        '& > div:focus': {
            backgroundColor: 'white'
        }
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main
    },
    tableHeaderCell: {
        color: 'white !important',
        fontWeight: 'bold'
    },
    actionsContainer: {
        display: 'flex',
        gap: theme.spacing(1)
    },
    tableWrapper: {
        marginBottom: theme.spacing(3)
    },
    courseFullValue: {
        display: 'inline',
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    courseShortValue: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom'
        }
    },
    ...containedButton(theme, false, false)
}));

export const criarSchemaDadosInscricao = (formulario = {}) => {
    const config = criarConfiguracaoFormulario(formulario.configuracoesFormulario);
    const cotasObrigatorio = config.isRequired(GRUPOS_FORMULARIO.PARTICIPA_COTAS);
    /* Só dá para exigir a escolha se houver algum processo para escolher. */
    const processoObrigatorio = config.isRequired(GRUPOS_FORMULARIO.INSCRICAO_PROCESSO)
        && (formulario.inscricoesProcessoSeletivo || []).length > 0;
    const categoriaObrigatoria = config.isRequired(GRUPOS_FORMULARIO.CATEGORIA);
    const escolaridadeObrigatoria = config.isRequired(GRUPOS_FORMULARIO.ESCOLARIDADE);
    const enemObrigatorio = config.isRequired(GRUPOS_FORMULARIO.GRUPO_NOTA_ENEM);
    const motivoObrigatorio = config.isRequired(GRUPOS_FORMULARIO.MOTIVO);
    return Yup.object().shape({
    vaga: Yup.object().nullable().required('Curso de interesse é obrigatório'),
    sistemaCotas: Yup.boolean().nullable().test(
        'cotas-obrigatorio', 'É necessário informar se participa do sistema de cotas',
        value => !cotasObrigatorio || value !== null && value !== undefined
    ),
    itemSelecionadoPsInscricao: Yup.mixed().test(
        'processo-obrigatorio', 'É necessário informar a inscrição do processo seletivo',
        value => !processoObrigatorio || value !== null && value !== undefined && String(value).trim() !== ''
    ),
    categoriaSelecionadoPsInscricao: Yup.string().test(
        'categoria-obrigatoria', 'É necessário informar uma categoria de inscrição',
        value => !categoriaObrigatoria || Boolean(String(value || '').trim())
    ),
    inscricaoEnsino: Yup.array().min(escolaridadeObrigatoria ? 1 : 0, 'É necessário informar a escolaridade'),
    motivo: Yup.string().test(
        'motivo-obrigatorio', 'Motivo é obrigatório',
        value => !motivoObrigatorio || Boolean(String(value || '').trim())
    ),
    inscricaoVeterano: Yup.object().shape({
        possuiEnem: Yup.boolean().nullable(),
        possuiBeneficioNaIes: Yup.boolean().nullable(),
        descricaoBeneficio: Yup.string().nullable(),
        participouProcessoAnterior: Yup.boolean().nullable(),
        descricaoProcessoAnterior: Yup.string().nullable()
    }).test('veterano-obrigatorio', 'Preencha os dados obrigatórios de veterano', value => {
        if (formulario.programaId !== 2 || !value) return true;
        const respostaValida = campo => value[campo] !== null && value[campo] !== undefined;
        return (!config.isRequired(GRUPOS_FORMULARIO.POSSUI_ENEM_VETERANO) || respostaValida('possuiEnem'))
            && (!config.isRequired(GRUPOS_FORMULARIO.POSSUI_BENEFICIO_VETERANO) || respostaValida('possuiBeneficioNaIes'))
            && (!config.isRequired(GRUPOS_FORMULARIO.POSSUI_BENEFICIO_VETERANO) || Boolean(String(value.descricaoBeneficio || '').trim()))
            && (!config.isRequired(GRUPOS_FORMULARIO.PARTICIPOU_PROCESSO_VETERANO) || respostaValida('participouProcessoAnterior'))
            && (!config.isRequired(GRUPOS_FORMULARIO.PARTICIPOU_PROCESSO_VETERANO) || Boolean(String(value.descricaoProcessoAnterior || '').trim()));
    }),
    inscricaoEnem: Yup.object().shape({
        utilizarEnem: Yup.boolean(),
        processoSeletivoEnem: Yup.string().when('utilizarEnem', {
            is: true,
            then: enemObrigatorio ? Yup.string().required('Ano do ENEM é obrigatório') : Yup.string().nullable(),
            otherwise: Yup.string().nullable()
        }),
        cdEnem: Yup.string().when('utilizarEnem', {
            is: true,
            then: enemObrigatorio ? Yup.string().required('Código do ENEM é obrigatório') : Yup.string().nullable(),
            otherwise: Yup.string().nullable()
        }),
        pontuacao: Yup.number().typeError('Deve ser um número').when('utilizarEnem', {
            is: true,
            then: enemObrigatorio ? Yup.number().required('Pontuação é obrigatória').max(1000, 'Pontuação máxima é 1000') : Yup.number().nullable(),
            otherwise: Yup.number().nullable()
        }),
        nomeArquivo: Yup.string().nullable().test(
            'comprovante-enem-obrigatorio', 'É necessário informar o arquivo do ENEM',
            function(value) {
                return !enemObrigatorio || this.parent.utilizarEnem !== true || Boolean(value);
            }
        )
    })
    });
};

const FormDadosInscricao = forwardRef((props, ref) => {
    const classes = useStyles();
    const tabsRef = useRef(null);
    const fetch = useFetch();
    const showErrorMessage = useErrorMessage();
    /* somenteConsulta: fora do período de inscrição a etapa abre só para consulta, nada pode ser alterado. */
    const { data, onUpdate, formulario = {}, activeSectionId, somenteConsulta = false } = props;
    const formularioConfig = criarConfiguracaoFormulario(formulario.configuracoesFormulario);
    const mostrarProcesso = formularioConfig.isVisible(GRUPOS_FORMULARIO.INSCRICAO_PROCESSO);
    const temProcessoSeletivo = (formulario.inscricoesProcessoSeletivo || []).length > 0;
    const mostrarCategoria = formularioConfig.isVisible(GRUPOS_FORMULARIO.CATEGORIA);
    const mostrarCotas = formularioConfig.isVisible(GRUPOS_FORMULARIO.PARTICIPA_COTAS);
    const setDadosResumo = useContextoResumoAcoes();
    const [vagas, setVagas] = useState([]);
    const [formacoes, setFormacoes] = useState([]);
    const [enems, setEnems] = useState([]);

    useEffect(() => {
        if (data) {
            const psId = data.idProcessoSeletivo
                      || data.itemSelecionadoPsInscricao
                      || data.vaga?.idProcessoSeletivo 
                      || data.vaga?.processoSeletivo?.id 
                      || data.psInscricao?.id 
                      || data.idProcessoSeletivo;

            if (psId) {
                fetch(`${URL_PROCESSO_SELETIVO}/${psId}/vagas`, {}, (response) => {
                    setVagas(response || []);
                });
                fetch(URL_PROCESSO_SELETIVO_ENEM(psId), {}, (response) => {
                    setEnems(response || []);
                });
            }
        }
    }, [data?.id, data?.itemSelecionadoPsInscricao, data?.vaga?.idProcessoSeletivo]);

    useEffect(() => {
        fetch(URL_FORMACOES, {}, (response) => {
            setFormacoes(response || []);
        });
    }, []);

    const getVagaDescricao = (v) => {
        if (!v || !v.curso) return '';
        const cursoNome = v.curso.descricao || v.curso.descricaoResumida || '';
        const turnoNome = v.turno && v.turno.descricao ? v.turno.descricao : 'EAD';
        const campusNome = v.campus && v.campus.nome ? v.campus.nome : '';

        return [cursoNome, turnoNome, campusNome]
            .map(item => String(item).trim())
            .filter(Boolean)
            .join(' - ');
    };

    const getVagaCursoNome = (v) => {
        if (!v || !v.curso) return '';
        return v.curso.descricao || v.curso.descricaoResumida || '';
    };

    const {
        dialogState,
        openDialog,
        closeDialog
    } = useDialogState({
        escolaridade: false
    });

    const [editEscolaIndex, setEditEscolaIndex] = useState(-1);

    const formEtapa = useFormularioInscricao({
        valoresIniciais: {
            ...data,
            // Certifica de inicializar utilizarEnem se não estiver setado
            inscricaoEnem: {
                id: data.inscricaoEnem?.id || null,
                utilizarEnem: typeof data.inscricaoEnem?.utilizarEnem === 'boolean' ? data.inscricaoEnem.utilizarEnem : data.utilizacaoNota === 'E',
                processoSeletivoEnem: data.inscricaoEnem?.processoSeletivoEnem?.id != null ? String(data.inscricaoEnem.processoSeletivoEnem.id) : (data.inscricaoEnem?.processoSeletivoEnem || ''),
                cdEnem: data.inscricaoEnem?.cdEnem || '',
                pontuacao: data.inscricaoEnem?.pontuacao != null ? data.inscricaoEnem.pontuacao : '',
                nomeArquivo: data.inscricaoEnem?.nomeArquivo || '',
                arquivo: data.inscricaoEnem?.arquivo || null,
                mimetype: data.inscricaoEnem?.mimetype || ''
            }
        },
        reinicializar: true,
        schema: criarSchemaDadosInscricao(formulario)
    });

    useImperativeHandle(ref, () => ({
        formulario: formEtapa.api
    }));

    useEffect(() => {
        if (setDadosResumo) {
            setDadosResumo(formEtapa.valores);
        }
    }, [formEtapa.valores, setDadosResumo]);

    useEffect(() => {
        if (formEtapa.submitCount > 0 && !formEtapa.valido) {
            const firstErrorIdx = tabContent.findIndex(item => item.hasError);
            if (firstErrorIdx !== -1) {
                tabsRef.current?.setTabIndex(firstErrorIdx);
            }
        }
    }, [formEtapa.submitCount, formEtapa.valido]);

    // --- Escolaridade CRUD ---
    const handleAddEscola = () => {
        setEditEscolaIndex(-1);
        openDialog('escolaridade')();
    };

    const handleSaveEscola = (form) => {
        const current = [...(formEtapa.valores.inscricaoEnsino || [])];
        // preserve id when editing existing record
        const existingId = editEscolaIndex > -1 ? current[editEscolaIndex]?.id : null;
        const item = {
            id: existingId || null,
            formacao: form.formacao || null,
            dsCurso: form.dsCurso || '',
            instituicao: form.instituicao || '',
            completo: form.completo || 'N',
            dataConclusao: form.dataConclusao || null,
            ensinoPublico: form.ensinoPublico || 'PU',
        };
        if (editEscolaIndex > -1) {
            current[editEscolaIndex] = item;
        } else {
            current.push(item);
        }
        formEtapa.definirValor('inscricaoEnsino', current);
        closeDialog('escolaridade')();
    };

    const handleEditEscola = (index) => {
        setEditEscolaIndex(index);
        openDialog('escolaridade')();
    };

    const handleDeleteEscola = (index) => {
        const current = [...(formEtapa.valores.inscricaoEnsino || [])];
        current.splice(index, 1);
        formEtapa.definirValor('inscricaoEnsino', current);
    };

    const getTipoEnsinoLabel = (code) => {
        const map = { PU: 'Pública', PI: 'Privada c/ bolsa integral', PP: 'Privada c/ bolsa parcial', PS: 'Privada s/ bolsa', P2: 'Pública / Privada' };
        return map[code] || code || '';
    };

    const escolaridadeColumns = [
        { label: 'Formação', field: 'formacao.descricao', valueGetter: (val) => val || '-' },
        { label: 'Curso', field: 'dsCurso', valueGetter: (val) => val || '-' },
        { label: 'Instituição', field: 'instituicao', valueGetter: (val) => val || '-' },
        { label: 'Situação', field: 'completo', valueGetter: (val) => val === 'S' ? 'Concluído' : (val === 'N' ? 'Em Andamento' : '-') },
        { label: 'Conclusão', field: 'dataConclusao', valueGetter: (val) => val || '-' },
        { label: 'Tipo de Ensino', field: 'ensinoPublico', valueGetter: (val) => getTipoEnsinoLabel(val) || '-' },
        ...(somenteConsulta ? [] : [criarColunaAcoes(row => {
            const idx = formEtapa.valores.inscricaoEnsino.indexOf(row);
            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditEscola(idx) },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, cor: 'secondary', onClick: () => handleDeleteEscola(idx) }
            ];
        }, { className: classes.actionsContainer })])
    ];

    const handleSelectEnemFile = async (file) => {
        let base64Data = null;
        try {
            base64Data = await arquivoParaBase64(file);
        } catch (e) {
            showErrorMessage('Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.');
            return;
        }

        formEtapa.definirValor('inscricaoEnem.nomeArquivo', file.name);
        formEtapa.definirValor('inscricaoEnem.arquivo', base64Data);
        formEtapa.definirValor('inscricaoEnem.mimetype', file.type || 'application/pdf');
    };

    const handleRemoveEnemFile = () => {
        formEtapa.definirValor('inscricaoEnem.nomeArquivo', '');
        formEtapa.definirValor('inscricaoEnem.arquivo', null);
        formEtapa.definirValor('inscricaoEnem.mimetype', '');
    };

    const sections = getStepSections(STEP_IDS.DADOS_INSCRICAO, { formulario, inscricao: formEtapa.valores });

    /*
     * Estado de erro de um bloco do formulário: destaca só o bloco afetado e,
     * quando o erro não tem campo próprio onde aparecer, lista o que falta.
     */
    const bloco = (secaoId, ...campos) => {
        if (!formEtapa.submitCount) return { erro: false, itens: [] };

        return {
            erro: temErroNosCampos(formEtapa.erros, campos),
            itens: listarErrosSecao(sections.find(secao => secao.id === secaoId), formEtapa.erros, formEtapa.valores, campos)
        };
    };

    const tabEntries = [
        {
            id: 'opcoes',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Opções de Inscrição</h4>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label='Curso de Interesse'>
                            <FormControl
                                variant="outlined"
                                fullWidth
                                disabled={somenteConsulta}
                                error={formEtapa.tocados.vaga && !!formEtapa.erros.vaga}>
                                <Select
                                    name="vaga"
                                    value={formEtapa.valores.vaga ? vagas.find(c => c.id === formEtapa.valores.vaga.id) || '' : ''}
                                    onChange={(e) => formEtapa.definirValor('vaga', e.target.value)}
                                    renderValue={(selected) => selected ? (
                                        <>
                                            <span className={classes.courseFullValue}>{getVagaDescricao(selected)}</span>
                                            <span className={classes.courseShortValue}>{getVagaCursoNome(selected)}</span>
                                        </>
                                    ) : 'Selecione...'}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {vagas.map(c => (
                                        <MenuItem key={c.id} value={c} title={getVagaDescricao(c)}>{getVagaDescricao(c)}</MenuItem>
                                    ))}
                                </Select>
                                {formEtapa.tocados.vaga && <FormHelperText>{formEtapa.erros.vaga}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    {mostrarCotas && (
                        <Grid item xs={12} sm={5}>
                            <LabelValue label='Participa do sistema de cotas? *'>
                                <FormControl component="fieldset" disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.sistemaCotas}>
                                    <RadioGroup
                                        name="sistemaCotas"
                                        value={String(formEtapa.valores.sistemaCotas ?? '')}
                                        onChange={(e) => formEtapa.definirValor('sistemaCotas', e.target.value === 'true')}
                                        row
                                    >
                                        <FormControlLabel value="true" control={<Radio color="primary" />} label="Sim" />
                                        <FormControlLabel value="false" control={<Radio color="primary" />} label="Não" />
                                    </RadioGroup>
                                    {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.sistemaCotas}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    )}
                </Grid>
            )
        },
        {
            id: 'processoCategoria',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Processo e Categoria</h4>
                    </Grid>
                    {mostrarProcesso && (
                        <Grid item xs={12}>
                            <LabelValue label="Inscrição processo seletivo">
                                <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={!!formEtapa.erros.itemSelecionadoPsInscricao}>
                                    <Select
                                        name="itemSelecionadoPsInscricao"
                                        value={formEtapa.valores.itemSelecionadoPsInscricao || ''}
                                        onChange={formEtapa.aoMudar}
                                        disabled={somenteConsulta || !temProcessoSeletivo}
                                        displayEmpty
                                    >
                                        {/* Sem processo disponível o campo não fica em branco: mostra e mantém "Nenhum processo". */}
                                        {temProcessoSeletivo ? (
                                            <MenuItem value=""><em>Selecione...</em></MenuItem>
                                        ) : (
                                            <MenuItem value="">Nenhum processo</MenuItem>
                                        )}
                                        {(formulario.inscricoesProcessoSeletivo || []).map(item => (
                                            <MenuItem key={item.id} value={String(item.id)}>{item.descricao}</MenuItem>
                                        ))}
                                    </Select>
                                    {!!formEtapa.erros.itemSelecionadoPsInscricao && <FormHelperText>{formEtapa.erros.itemSelecionadoPsInscricao}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    )}
                    {mostrarCategoria && (
                        <Grid item xs={12}>
                            <LabelValue label="Categoria">
                                <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={!!formEtapa.erros.categoriaSelecionadoPsInscricao}>
                                    <Select
                                        name="categoriaSelecionadoPsInscricao"
                                        value={formEtapa.valores.categoriaSelecionadoPsInscricao || ''}
                                        onChange={formEtapa.aoMudar}
                                    >
                                        <MenuItem value=""><em>Selecione...</em></MenuItem>
                                        {(formulario.categoriasInscricao || []).map(item => (
                                            <MenuItem key={item} value={item}>{item}</MenuItem>
                                        ))}
                                    </Select>
                                    {!!formEtapa.erros.categoriaSelecionadoPsInscricao && <FormHelperText>{formEtapa.erros.categoriaSelecionadoPsInscricao}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    )}
                </Grid>
            )
        },
        {
            id: 'veterano',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Dados de Veterano</h4>
                        {formEtapa.submitCount > 0 && !!formEtapa.erros.inscricaoVeterano && (
                            <FormHelperText error>{formEtapa.erros.inscricaoVeterano}</FormHelperText>
                        )}
                    </Grid>
                    {formularioConfig.isVisible(GRUPOS_FORMULARIO.POSSUI_ENEM_VETERANO) && (
                        <Grid item xs={12}>
                            <LabelValue label="Possui ENEM">
                                <RadioGroup row name="inscricaoVeterano.possuiEnem" value={String(formEtapa.valores.inscricaoVeterano?.possuiEnem ?? '')} onChange={formEtapa.aoMudar}>
                                    <FormControlLabel value="true" disabled={somenteConsulta} control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value="false" disabled={somenteConsulta} control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                            </LabelValue>
                        </Grid>
                    )}
                    {formularioConfig.isVisible(GRUPOS_FORMULARIO.POSSUI_BENEFICIO_VETERANO) && (
                        <Grid item xs={12}>
                            <LabelValue label="Possui benefício na Instituição de Ensino">
                                <RadioGroup row name="inscricaoVeterano.possuiBeneficioNaIes" value={String(formEtapa.valores.inscricaoVeterano?.possuiBeneficioNaIes ?? '')} onChange={formEtapa.aoMudar}>
                                    <FormControlLabel value="true" disabled={somenteConsulta} control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value="false" disabled={somenteConsulta} control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                                {formEtapa.valores.inscricaoVeterano?.possuiBeneficioNaIes === true && (
                                    <OutlinedInput fullWidth disabled={somenteConsulta} name="inscricaoVeterano.descricaoBeneficio" value={formEtapa.valores.inscricaoVeterano?.descricaoBeneficio || ''} onChange={formEtapa.aoMudar} />
                                )}
                            </LabelValue>
                        </Grid>
                    )}
                    {formularioConfig.isVisible(GRUPOS_FORMULARIO.PARTICIPOU_PROCESSO_VETERANO) && (
                        <Grid item xs={12}>
                            <LabelValue label="Participou de algum processo de seleção anterior">
                                <RadioGroup row name="inscricaoVeterano.participouProcessoAnterior" value={String(formEtapa.valores.inscricaoVeterano?.participouProcessoAnterior ?? '')} onChange={formEtapa.aoMudar}>
                                    <FormControlLabel value="true" disabled={somenteConsulta} control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value="false" disabled={somenteConsulta} control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                                {formEtapa.valores.inscricaoVeterano?.participouProcessoAnterior === true && (
                                    <OutlinedInput fullWidth disabled={somenteConsulta} name="inscricaoVeterano.descricaoProcessoAnterior" value={formEtapa.valores.inscricaoVeterano?.descricaoProcessoAnterior || ''} onChange={formEtapa.aoMudar} />
                                )}
                            </LabelValue>
                        </Grid>
                    )}
                </Grid>
            )
        },
        {
            id: 'escolaridade',
            content: (
                <BlocoErro {...bloco('escolaridade', 'inscricaoEnsino')}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Escolaridade</h4>
                    </Grid>
                    <Grid item xs={12}>
                        {!somenteConsulta && (
                            <ToolBar>
                                <Tooltip title="Adicionar" arrow>
                                    <Button
                                        variant="contained"
                                        className={classes.containedButton}
                                        startIcon={<Add />}
                                        onClick={handleAddEscola}
                                    >
                                        Adicionar
                                    </Button>
                                </Tooltip>
                            </ToolBar>
                        )}
                        <div className={classes.tableWrapper}>
                            <DataGrid
                                responsive="card"
                                tableContainerProps={{
                                    component: 'div'
                                }}
                                rows={formEtapa.valores.inscricaoEnsino || []}
                                columns={escolaridadeColumns}
                                comparatorKeys={['formacao.descricao']}
                                paginate
                                paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                                noRecordsText="Nenhum grau de ensino cadastrado."
                            />
                        </div>
                    </Grid>
                </Grid>
                </BlocoErro>
            )
        },
        {
            id: 'enem',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Pontuação do ENEM</h4>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label='Optar por utilizar a média do ENEM? *'>
                            <FormControl component="fieldset" disabled={somenteConsulta}>
                                <RadioGroup
                                    name="inscricaoEnem.utilizarEnem"
                                    value={formEtapa.valores.inscricaoEnem.utilizarEnem.toString()}
                                    onChange={(e) => {
                                        const val = e.target.value === 'true';
                                        formEtapa.definirValor('inscricaoEnem.utilizarEnem', val);
                                        // Mantém utilizacaoNota coerente: uma reinicialização não pode reverter a escolha do rádio.
                                        formEtapa.definirValor('utilizacaoNota', val ? 'E' : (formEtapa.valores.utilizacaoNota === 'H' ? 'H' : 'P'));
                                        if (!val) {
                                            // Reset fields
                                            formEtapa.definirValor('inscricaoEnem.cdEnem', '');
                                            formEtapa.definirValor('inscricaoEnem.pontuacao', '');
                                            formEtapa.definirValor('inscricaoEnem.processoSeletivoEnem', '');
                                            handleRemoveEnemFile();
                                        }
                                    }}
                                    row
                                >
                                    <FormControlLabel value="true" control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value="false" control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {formEtapa.valores.inscricaoEnem.utilizarEnem && (
                        <>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='Ano do ENEM *'>
                                    <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.inscricaoEnem?.processoSeletivoEnem && !!formEtapa.erros.inscricaoEnem?.processoSeletivoEnem}>
                                        <Select
                                            name="inscricaoEnem.processoSeletivoEnem"
                                            value={formEtapa.valores.inscricaoEnem.processoSeletivoEnem}
                                            onChange={formEtapa.aoMudar}
                                        >
                                            <MenuItem value=""><em>Selecione...</em></MenuItem>
                                            {enems.map((item) => (
                                                <MenuItem key={item.id} value={String(item.id)}>
                                                    ENEM {item.enem?.anoEnem || item.id}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formEtapa.tocados.inscricaoEnem?.processoSeletivoEnem && <FormHelperText>{formEtapa.erros.inscricaoEnem?.processoSeletivoEnem}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='Código do ENEM *'>
                                    <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.inscricaoEnem?.cdEnem && !!formEtapa.erros.inscricaoEnem?.cdEnem}>
                                        <OutlinedInput
                                            name="inscricaoEnem.cdEnem"
                                            value={formEtapa.valores.inscricaoEnem.cdEnem}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.inscricaoEnem?.cdEnem && <FormHelperText>{formEtapa.erros.inscricaoEnem?.cdEnem}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='Pontuação Média *'>
                                    <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.inscricaoEnem?.pontuacao && !!formEtapa.erros.inscricaoEnem?.pontuacao}>
                                        <OutlinedInput
                                            name="inscricaoEnem.pontuacao"
                                            type="number"
                                            value={formEtapa.valores.inscricaoEnem.pontuacao}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                            placeholder="0.00"
                                        />
                                        {formEtapa.tocados.inscricaoEnem?.pontuacao && <FormHelperText>{formEtapa.erros.inscricaoEnem?.pontuacao}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12}>
                                <LabelValue label='Comprovante de Nota do ENEM (PDF) *'>
                                    <FormControl fullWidth disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.inscricaoEnem?.nomeArquivo}>
                                        {somenteConsulta ? (
                                            <OutlinedInput
                                                fullWidth
                                                disabled
                                                value={formEtapa.valores.inscricaoEnem.nomeArquivo || ''}
                                                placeholder="Nenhum arquivo enviado"
                                            />
                                        ) : (
                                            <FileSelector
                                                onSelect={handleSelectEnemFile}
                                                onRemove={handleRemoveEnemFile}
                                                onFile={formEtapa.valores.inscricaoEnem.nomeArquivo ? { nome: formEtapa.valores.inscricaoEnem.nomeArquivo } : null}
                                                htmlFor="enemFile"
                                                maxSize={5 * 1024 * 1024}
                                            />
                                        )}
                                        {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.inscricaoEnem?.nomeArquivo}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}
                </Grid>
            )
        },
        {
            id: 'motivacao',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Motivação</h4>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label='Descreva o motivo pelo qual você busca obter o benefício social: *'>
                            <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.motivo && !!formEtapa.erros.motivo}>
                                <OutlinedInput
                                    multiline
                                    rows={5}
                                    name="motivo"
                                    value={formEtapa.valores.motivo}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    className={classes.textMulti}
                                    placeholder="Descreva aqui o motivo..."
                                />
                                {formEtapa.tocados.motivo && <FormHelperText>{formEtapa.erros.motivo}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                </Grid>
            )
        }
    ];

    const tabContent = buildTabContent(sections, tabEntries, formEtapa);

    return (
        <form className={classes.root} noValidate autoComplete="off">
            <GenericTab
                ref={tabsRef}
                orientation="vertical"
                noAppBar
                mobileVariant="accordion"
                indicatorColor="primary"
                textColor="primary"
                scrollButtons="auto"
                className={classes.tabs}
                activeId={activeSectionId}
                content={tabContent}
            />

            {/* --- DIALOG MODAL --- */}
            <DialogEscolaridade
                open={dialogState.escolaridade}
                onClose={closeDialog('escolaridade')}
                onSave={handleSaveEscola}
                initialValues={editEscolaIndex > -1 ? formEtapa.valores.inscricaoEnsino[editEscolaIndex] : null}
                formacoes={formacoes}
            />
        </form>
    );
});

FormDadosInscricao.displayName = 'FormDadosInscricao';
export default FormDadosInscricao;
